import React from 'react';
import { motion } from 'framer-motion';

import FadeIn from '../../../components/animations/FadeIn';
import LazyVideo from '../../../components/LazyVideo';
import { useGoogleStats } from '../../../hooks/useGoogleStats';

// "Recent installs" proof-gallery section (2026-05-22). Sits directly below
// PricingComparisonSection on the homepage. Three-pass build:
//   Pass 1 (this file's current state) — STRUCTURE + LAYOUT ONLY.
//     Skeleton grid, placeholder tiles, basic typography. No premium polish,
//     no animations, no real media. Mike verifies the responsive layout
//     reads cleanly before we invest in styling.
//   Pass 2 — visual look-and-feel (gradients, glows, real Google wordmark,
//     elevated card treatments, etc.).
//   Pass 3 — animations + wire in the real install video and photos.
//
// Mobile: the large video tile goes full-width on top; the four photo tiles
// fall into a 2×2 grid below it (achieved with a 2-col mobile grid + the
// video tile carrying col-span-2). On lg+ the layout flips to a 4-col grid
// where the video spans cols 1-2 × rows 1-2, photos take 1×1 cells.
//
// Data structure intentionally simple — Pass 3 swaps placeholder media for
// real video/photo URLs, no JSX changes needed.

// ─── Data ────────────────────────────────────────────────────────────────

// Photo tiles use real drone aerials (1600×900 WebP, optimized via
// scripts/optimize-recent-installs.js). The aerials are 16:9, the tiles
// render 4:3 — `object-cover` crops the sides. `objectPosition` is per-tile
// in case any image needs a vertical bias to keep the roof in frame; the
// default 'center' is correct for the typical drone shot where the house
// is centered. Video tile (Beavercreek) is still a placeholder pending the
// real install video.
const galleryTiles = [
  {
    id: 'beavercreek-video',
    type: 'video',
    src: '/videos/install-beavercreek.mp4',
    poster: '/images/install-beavercreek-poster.webp',
    location: 'Beavercreek, OH',
    caption: 'Beavercreek, OH · GAF Timberline HDZ',
  },
  { id: 'springboro',  type: 'photo', src: '/images/installs/springboro.webp',  objectPosition: 'center', location: 'Springboro, OH',  caption: 'Springboro, OH' },
  { id: 'troy',        type: 'photo', src: '/images/installs/troy.webp',        objectPosition: 'center', location: 'Troy, OH',        caption: 'Troy, OH' },
  { id: 'germantown',  type: 'photo', src: '/images/installs/germantown.webp',  objectPosition: 'center', location: 'Germantown, OH',  caption: 'Germantown, OH' },
  { id: 'centerville', type: 'photo', src: '/images/installs/centerville.webp', objectPosition: 'center', location: 'Centerville, OH', caption: 'Centerville, OH' },
];

const videoTile = galleryTiles.find((t) => t.type === 'video');
const photoTiles = galleryTiles.filter((t) => t.type === 'photo');

const featuredQuote = {
  text:
    'Transparent pricing and fast honest service and delivery of goods! OpenbooQ by far had the best price on a replacement roof. It looks great with a comparable warranty and they cleaned up after the one day job great!',
  reviewerName: 'Donavan Schlunt',
  reviewerInitial: 'D',
  reviewerLocation: 'Cincinnati, OH',
  reviewerLabel: 'Verified Google review',
};

// Last-known fallback values shown during the brief window before
// useGoogleStats resolves on first page load (and as a safety net if the
// fetch fails). Match the current production aggregate so the card never
// flashes a worse number. Live values from `useGoogleStats` replace these
// the moment the Supabase fetch lands.
const FALLBACK_RATING = 5.0;
const FALLBACK_TOTAL_REVIEWS = 32;
const GOOGLE_REVIEWS_HREF = '/reviews'; // same destination GoogleReviewsBadge uses

// ─── Helpers ─────────────────────────────────────────────────────────────

// Caption pill — iOS-style frosted glass so it reads cleanly over any
// part of the photo (dark roof, bright sky, mid-tone siding). The
// translucent white + backdrop-blur + saturate combo blurs the photo
// behind the pill while keeping its colors slightly punched up, so the
// text always has high contrast against a softened, color-coherent
// backdrop. Drop shadow grounds it against bright-sky areas where a
// hairline border alone would disappear.
const CaptionPill = ({ children }) => (
  <span
    className="absolute bottom-3 left-3 z-10 inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap"
    style={{
      background: 'rgba(255, 255, 255, 0.78)',
      color: 'rgba(15, 23, 42, 0.92)',
      border: '1px solid rgba(255, 255, 255, 0.55)',
      backdropFilter: 'blur(12px) saturate(160%)',
      WebkitBackdropFilter: 'blur(12px) saturate(160%)',
      boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
    }}
  >
    {children}
  </span>
);

// Shared visual language for the gallery tiles. Same edge / shadow / hover
// vocabulary on both the video tile and the four photo tiles so the grid
// reads as one unit. Resting state: a barely-there cyan ring + soft slate
// elevation — frames the photo without competing with it. Hover state:
// lift, cyan ring intensifies, cyan glow emerges under the dark shadow,
// inner media zooms ~4% within the clipped frame. Framer Motion's
// `whileHover` is gated to hover-capable devices automatically, so touch
// taps don't trigger a stuck hover state on mobile.
const TILE_REST_SHADOW =
  'inset 0 0 0 1px rgba(7, 176, 233, 0.12),' +
  ' 0 1px 2px rgba(15, 23, 42, 0.05),' +
  ' 0 10px 28px rgba(15, 23, 42, 0.07)';

const TILE_HOVER_SHADOW =
  'inset 0 0 0 1px rgba(7, 176, 233, 0.35),' +
  ' 0 2px 6px rgba(15, 23, 42, 0.07),' +
  ' 0 4px 12px rgba(15, 23, 42, 0.10),' +
  ' 0 20px 50px rgba(7, 176, 233, 0.18)';

// Entrance choreography (Pass 3 added on top of Pass 2's hover treatment).
// One parent variant trigger on the gallery grid drives the whole cascade
// via `delayChildren` + `staggerChildren`, instead of putting the delay
// inside each tile's `visible` variant — that's what stops the cascade
// from replaying every time the user hovers off a tile (whileHover →
// visible would re-apply any per-variant delay otherwise). Tile states:
//   hidden  — initial entrance state (opacity 0, slight y-offset, but the
//             resting box-shadow is already set so it doesn't flash in)
//   visible — Pass 2's "rest" state, renamed for semantic accuracy. Idle
//             AND the unhover target.
//   hover   — Pass 2's hover treatment, unchanged.
// `once: true` on the parent viewport observer means this fires exactly
// once per page load and never replays.
const EASE_OUT_EXPO = [0.16, 1, 0.3, 1];
const GALLERY_DELAY_CHILDREN = 0.32; // tiles start after the header animates
const GALLERY_STAGGER = 0.10;        // ~100ms between tiles

const galleryParentVariants = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: GALLERY_DELAY_CHILDREN,
      staggerChildren: GALLERY_STAGGER,
    },
  },
};

const tileVariants = {
  hidden: { opacity: 0, y: 16, boxShadow: TILE_REST_SHADOW },
  visible: {
    opacity: 1,
    y: 0,
    boxShadow: TILE_REST_SHADOW,
    transition: { duration: 0.55, ease: EASE_OUT_EXPO },
  },
  hover: {
    y: -4,
    boxShadow: TILE_HOVER_SHADOW,
    transition: { duration: 0.35, ease: EASE_OUT_EXPO },
  },
};

const mediaVariants = {
  hidden: { scale: 1 },
  visible: { scale: 1 },
  hover: { scale: 1.04, transition: { duration: 0.45, ease: EASE_OUT_EXPO } },
};

const StarRow = ({ size = 16, rating = 5 }) => {
  // Filled-count rule mirrors the existing GoogleReviewsBadge: round to the
  // nearest whole star (no half-fills). At 5.0 → 5 filled; at 4.6 → 5; at
  // 4.4 → 4. Keeps the visual treatment consistent across the site.
  const filled = Math.round(rating);
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <svg
          key={i}
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill={i < filled ? '#FBBF24' : 'none'}
          stroke={i < filled ? '#F59E0B' : '#D1D5DB'}
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
        </svg>
      ))}
    </div>
  );
};

// Google "G" mark — same path the floating GoogleReviewsBadge widget uses,
// so the visual treatment stays consistent across the site.
const GoogleG = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.33 0 9.25-3.65 9.25-9.09 0-1.15-.15-1.81-.15-1.81z"
      fill="#4285F4"
    />
  </svg>
);

// ─── Tile components ────────────────────────────────────────────────────

// Real install video — viewport-lazy since 2026-07-18. The old
// `<motion.video autoPlay preload="metadata">` downloaded the full 2.5MB
// mp4 on page load (autoplay overrides the preload hint). LazyVideo now
// shows the poster (same last-frame WebP, identical box → zero layout
// shift) and only attaches the src + starts muted/playsInline autoplay
// when the tile nears the viewport. Under prefers-reduced-motion it
// stays on the poster with a tap-to-play button instead of autoplaying.
// Still no `loop`, no `controls`: on ended, the browser freezes on the
// last frame, which matches the poster — no jump, no flash to black.
const VideoTile = ({ tile }) => (
  // Inherits hidden/visible state from the gallery parent's variant trigger;
  // whileHover overrides locally. LazyVideo forwards mediaVariants to its
  // internal motion.video so the hover zoom keeps working.
  <motion.div
    variants={tileVariants}
    whileHover="hover"
    className="relative rounded-2xl overflow-hidden bg-gray-200 aspect-video lg:aspect-auto lg:col-span-2 lg:row-span-2 col-span-2"
  >
    <LazyVideo
      variants={mediaVariants}
      src={tile.src}
      poster={tile.poster}
      className="absolute inset-0 w-full h-full object-cover"
      aria-label={`OpenbooQ install video — ${tile.location}`}
    />
    {/* Caption pill intentionally omitted on the video tile (2026-05-24).
        The four photo tiles below still carry their location captions
        via <CaptionPill> — only this hero video tile is uncaptioned so
        the install footage reads as the visual focus. tile.caption is
        kept in the data object as a stable aria-label source and for
        any future reintroduction. */}
  </motion.div>
);

const PhotoTile = ({ tile }) => (
  <motion.div
    variants={tileVariants}
    whileHover="hover"
    className="relative rounded-2xl overflow-hidden bg-gray-200 aspect-[4/3]"
  >
    <motion.img
      variants={mediaVariants}
      src={tile.src}
      alt={`OpenbooQ install in ${tile.location}`}
      loading="lazy"
      className="absolute inset-0 w-full h-full object-cover"
      style={{ objectPosition: tile.objectPosition || 'center' }}
    />
    <CaptionPill>{tile.caption}</CaptionPill>
  </motion.div>
);

// ─── Section ────────────────────────────────────────────────────────────

const RecentInstallsSection = () => {
  // Live aggregate from the same source GoogleReviewsBadge uses (module-level
  // cache in the hook means one fetch per page load). Falls back to last-known
  // values during the brief window before the fetch resolves.
  const { stats } = useGoogleStats();
  const liveRating = stats?.overallRating ?? FALLBACK_RATING;
  const liveTotalReviews = stats?.totalReviews ?? FALLBACK_TOTAL_REVIEWS;
  const formattedRating = Number(liveRating).toFixed(1);
  const formattedTotal = Math.floor(liveTotalReviews).toLocaleString('en-US');

  return (
  <section className="bg-white py-16 lg:py-24">
    <div className="max-w-[1200px] mx-auto px-5 lg:px-8">

      {/* A. Section header — three FadeIns staggered 80ms apart. Each
            FadeIn carries its own viewport observer; because the eyebrow /
            h2 / subhead are stacked tight, all three observers fire near-
            simultaneously when the user scrolls into the section, so the
            cascade reads as one orchestrated reveal. */}
      <div className="text-center mb-10 lg:mb-12">
        <FadeIn
          as="p"
          delay={0}
          duration={0.4}
          className="text-xs font-bold uppercase tracking-[0.2em] mb-4"
          style={{ color: '#07B0E9' }}
        >
          Real roofs, real homes
        </FadeIn>
        <FadeIn
          as="h2"
          delay={0.08}
          duration={0.5}
          className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 leading-[1.1] tracking-tight"
          style={{ color: '#000150', fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.02em' }}
        >
          Recent installs in <span style={{ color: '#07B0E9' }}>your area</span>.
        </FadeIn>
        <FadeIn
          as="p"
          delay={0.18}
          duration={0.45}
          className="text-gray-500 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed"
        >
          Every home below is a real OpenbooQ install. No stock photos. No staging.
        </FadeIn>
      </div>

      {/* B. Gallery grid — large video tile + four photo tiles.
            Mobile (default): 2-col grid, video col-spans both (full width),
              photos fall into a 2×2 below.
            Desktop (lg+): 4-col 2-row grid, video col-span-2 row-span-2,
              each photo takes 1×1. Heights match because photo cells use
              aspect-[4/3] which establishes the row height; the video cell
              fills its 2×2 span. */}
      <motion.div
        variants={galleryParentVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.15 }}
        className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-5"
      >
        <VideoTile tile={videoTile} />
        {photoTiles.map((tile) => (
          <PhotoTile key={tile.id} tile={tile} />
        ))}
      </motion.div>

      {/* C. Source line */}
      <p className="text-center text-xs text-gray-400 mb-10 lg:mb-12 max-w-2xl mx-auto leading-relaxed">
        Real install photos uploaded by OpenbooQ field team — gallery refreshes weekly.
      </p>

      {/* D. Google review summary + featured quote — narrower Google card
            on the left, wider quote card on the right (desktop); stacked
            on mobile (Google summary first, then quote — same reading
            order as the source attribution → social proof flow). */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-6">

        {/* LEFT: Google review summary (1/3 width on desktop) */}
        <article
          className="lg:col-span-1 rounded-2xl p-6 lg:p-7 flex flex-col items-start gap-3 bg-white"
          style={{
            border: '1px solid #E5E7EB',
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.04)',
          }}
        >
          <div className="flex items-center gap-2.5">
            <GoogleG size={26} />
            <span className="text-base font-bold text-gray-800">Google</span>
          </div>
          <div className="flex items-baseline gap-3 mt-1">
            <span className="text-5xl font-black text-gray-900 leading-none tracking-tight">
              {formattedRating}
            </span>
            <StarRow size={18} rating={liveRating} />
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">
            <strong className="font-bold text-gray-900">{formattedTotal} verified reviews</strong>
            {' · '}
            100% 5-star
          </p>
          <a
            href={GOOGLE_REVIEWS_HREF}
            className="inline-flex items-center gap-1.5 text-sm font-semibold mt-1"
            style={{ color: '#07B0E9' }}
          >
            Read every review
            <span aria-hidden="true">→</span>
          </a>
        </article>

        {/* RIGHT: Featured customer quote (2/3 width on desktop) */}
        <article
          className="lg:col-span-2 rounded-2xl p-6 lg:p-8 flex flex-col gap-4 bg-white"
          style={{
            border: '1px solid #E5E7EB',
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.04)',
          }}
        >
          <StarRow size={16} />
          <blockquote className="text-base lg:text-lg text-gray-700 leading-relaxed">
            “{featuredQuote.text}”
          </blockquote>
          <footer className="flex items-center gap-3 mt-2">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-base"
              style={{ background: 'linear-gradient(135deg, #07B0E9 0%, #0066A8 100%)' }}
              aria-hidden="true"
            >
              {featuredQuote.reviewerInitial}
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-bold text-gray-900">{featuredQuote.reviewerName}</span>
              <span className="text-xs text-gray-500 mt-0.5">
                {featuredQuote.reviewerLocation} · {featuredQuote.reviewerLabel}
              </span>
            </div>
          </footer>
        </article>
      </div>

    </div>
  </section>
  );
};

export default RecentInstallsSection;
